import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { authApi } from '../services/api';
import { Employee } from '../types';

interface AuthContextType {
  employee: Employee | null;
  isAuthenticated: boolean;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [employee, setEmployee] = useState<Employee | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if user is already logged in
    const token = localStorage.getItem('authToken');
    const savedEmployee = localStorage.getItem('employee');

    if (token && savedEmployee) {
      try {
        const employeeData = JSON.parse(savedEmployee);
        setEmployee(employeeData);
      } catch (error) {
        console.error('Error parsing saved employee data:', error);
        localStorage.removeItem('authToken');
        localStorage.removeItem('employee');
      }
    }
    setLoading(false);
  }, []);

  const login = async (email: string, password: string) => {
    const response = await authApi.login(email, password);
    localStorage.setItem('authToken', response.token);
    localStorage.setItem('employee', JSON.stringify(response.employee));
    setEmployee(response.employee);
  };

  const logout = async () => {
    await authApi.logout();
    setEmployee(null);
  };

  const value: AuthContextType = {
    employee,
    isAuthenticated: !!employee,
    loading,
    login,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};


